<template>
  <div class="w-full p-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Timecard Managers</h2>
      <button
        class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white hover:bg-accent-secondary"
        @click="openAssignModal()"
      >
        Set Manager
      </button>
    </div>

    <!-- Unassigned users callout -->
    <div
      v-if="!loading && unassignedUsers.length > 0"
      class="mb-6 p-3 rounded-md bg-alert-primary/10 border border-alert-primary/40"
    >
      <div class="text-sm font-medium text-alert-primary mb-2">
        {{ unassignedUsers.length }} user<span v-if="unassignedUsers.length !== 1">s</span> without an active timecard manager — submitted timecards for these users fall back to the default recipient.
      </div>
      <ul class="text-sm ml-4 list-disc">
        <li v-for="user in unassignedUsers" :key="user.id" class="flex items-center gap-3 py-0.5">
          <span>{{ user.full_name }} <span class="text-gray-500">{{ user.username }}</span></span>
          <button
            class="text-xs px-2 py-0.5 rounded bg-dark-primary text-white hover:bg-accent-primary"
            @click="openAssignModal(user)"
          >
            Assign
          </button>
        </li>
      </ul>
    </div>

    <!-- Filters -->
    <div class="flex flex-row flex-wrap gap-3 mb-3 items-center text-sm">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search user or manager..."
        class="p-1.5 border border-gray-400 rounded-md w-full sm:w-64"
      />
      <label class="flex items-center gap-1">
        <input type="checkbox" v-model="showInactive" />
        Include inactive history
      </label>
      <span class="ml-auto text-gray-500">{{ filteredRelationships.length }} row<span v-if="filteredRelationships.length !== 1">s</span></span>
    </div>

    <!-- Mobile card list (narrow screens) -->
    <div class="md:hidden space-y-2">
      <div v-if="loading" class="p-4 text-center text-gray-500 text-sm">Loading...</div>
      <div v-else-if="filteredRelationships.length === 0" class="p-4 text-center text-gray-500 text-sm">
        No timecard manager relationships match your filters.
      </div>
      <div
        v-for="rel in filteredRelationships"
        :key="`m-${rel.id}`"
        class="border border-gray-300 rounded-md bg-white p-3"
        :class="rel.is_active ? '' : 'text-gray-400'"
      >
        <div class="flex items-start justify-between gap-2 mb-1">
          <div class="min-w-0">
            <div class="text-xs text-gray-500 uppercase tracking-wide">Team Member</div>
            <div class="text-sm font-semibold truncate">{{ fullName(rel.user) }}</div>
            <div class="text-xs text-gray-500 truncate">{{ rel.user.username }}</div>
          </div>
          <span
            v-if="rel.is_active"
            class="px-2 py-0.5 text-xs rounded bg-accent-primary/10 text-accent-primary flex-shrink-0"
          >active</span>
          <span v-else class="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-500 flex-shrink-0">inactive</span>
        </div>
        <div class="mt-2">
          <div class="text-xs text-gray-500 uppercase tracking-wide">Manager</div>
          <div class="text-sm truncate">{{ fullName(rel.manager) }}</div>
          <div class="text-xs text-gray-500 truncate">{{ rel.manager.username }}</div>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          Since {{ formatDate(rel.updated_at) }}
          · Set by {{ rel.created_by ? fullName(rel.created_by) : '—' }}
        </div>
        <div v-if="rel.is_active" class="mt-3 flex flex-wrap gap-2">
          <button
            class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
            @click="openAssignModal(rel.user)"
          >Change Manager</button>
          <button
            class="text-xs px-2 py-1 border border-alert-primary text-alert-primary rounded hover:bg-alert-primary hover:text-white"
            @click="confirmDeactivate(rel)"
          >Deactivate</button>
        </div>
      </div>
    </div>

    <!-- Desktop / tablet table -->
    <div class="hidden md:block border border-gray-400 rounded-md overflow-hidden">
      <table class="table-auto w-full text-sm">
        <thead class="bg-dark-primary text-white">
          <tr>
            <th class="text-left px-3 py-2">Team Member</th>
            <th class="text-left px-3 py-2">Manager</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-left px-3 py-2">Since</th>
            <th class="text-left px-3 py-2">Set By</th>
            <th class="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">Loading...</td>
          </tr>
          <tr v-else-if="filteredRelationships.length === 0">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">No timecard manager relationships match your filters.</td>
          </tr>
          <tr
            v-for="rel in filteredRelationships"
            :key="rel.id"
            class="border-t border-gray-200"
            :class="rel.is_active ? '' : 'text-gray-400'"
          >
            <td class="px-3 py-2">
              {{ fullName(rel.user) }}
              <div class="text-xs text-gray-500">{{ rel.user.username }}</div>
            </td>
            <td class="px-3 py-2">
              {{ fullName(rel.manager) }}
              <div class="text-xs text-gray-500">{{ rel.manager.username }}</div>
            </td>
            <td class="px-3 py-2">
              <span
                v-if="rel.is_active"
                class="px-2 py-0.5 text-xs rounded bg-accent-primary/10 text-accent-primary"
              >active</span>
              <span v-else class="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-500">inactive</span>
            </td>
            <td class="px-3 py-2">{{ formatDate(rel.updated_at) }}</td>
            <td class="px-3 py-2">{{ rel.created_by ? fullName(rel.created_by) : '—' }}</td>
            <td class="px-3 py-2 text-right">
              <button
                v-if="rel.is_active"
                class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
                @click="openAssignModal(rel.user)"
              >Change Manager</button>
              <button
                v-if="rel.is_active"
                class="ml-2 text-xs px-2 py-1 border border-alert-primary text-alert-primary rounded hover:bg-alert-primary hover:text-white"
                @click="confirmDeactivate(rel)"
              >Deactivate</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Assign modal -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-md shadow-lg p-5 w-[28rem]">
        <h3 class="text-base font-semibold mb-3">Set Timecard Manager</h3>

        <label class="block text-sm mb-1">Team Member</label>
        <select
          v-model="modalUserId"
          :disabled="modalLockedUser"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-3"
        >
          <option :value="null" disabled>Select user...</option>
          <option v-for="u in allUsers" :key="u.id" :value="u.id">
            {{ userLabel(u) }}
          </option>
        </select>

        <label class="block text-sm mb-1">Manager</label>
        <select
          v-model="modalManagerId"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-3"
        >
          <option :value="null" disabled>Select manager...</option>
          <option
            v-for="u in allUsers"
            :key="u.id"
            :value="u.id"
            :disabled="u.id === modalUserId"
          >
            {{ userLabel(u) }}
          </option>
        </select>

        <div
          v-if="modalCurrentManagerName"
          class="text-xs text-gray-600 mb-3"
        >
          Current manager: {{ modalCurrentManagerName }} (will be deactivated)
        </div>

        <div v-if="modalError" class="text-sm text-alert-primary mb-2">{{ modalError }}</div>

        <div class="flex justify-end gap-2">
          <button
            class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100"
            @click="closeModal"
          >Cancel</button>
          <button
            class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
            :disabled="!modalUserId || !modalManagerId || modalSaving"
            @click="submitModal"
          >{{ modalSaving ? 'Saving...' : 'Save' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "timecard-manager-relationships-admin",
};
</script>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  getTimecardManagerRelationships,
  createTimecardManagerRelationship,
  deactivateTimecardManagerRelationship,
  getUnassignedTimecardManagerUsers,
} from "@/services/TimecardManagerRelationship.js";
import { getAllUsersWithGroups } from "@/services/User.js";

const store = useStore();
const $toast = useToast();

const loading = ref(false);
const relationships = ref([]);
const unassignedUsers = ref([]);
const allUsers = ref([]);

const searchQuery = ref("");
const showInactive = ref(false);

const modalOpen = ref(false);
const modalUserId = ref(null);
const modalManagerId = ref(null);
const modalLockedUser = ref(false);
const modalError = ref("");
const modalSaving = ref(false);

const activeByUserId = computed(() => {
  const map = {};
  for (const rel of relationships.value) {
    if (rel.is_active) map[rel.user.id] = rel;
  }
  return map;
});

const modalCurrentManagerName = computed(() => {
  if (!modalUserId.value) return null;
  const existing = activeByUserId.value[modalUserId.value];
  return existing ? fullName(existing.manager) : null;
});

const filteredRelationships = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return relationships.value.filter((rel) => {
    if (!showInactive.value && !rel.is_active) return false;
    if (!q) return true;
    const userName = fullName(rel.user).toLowerCase();
    const managerName = fullName(rel.manager).toLowerCase();
    const userU = (rel.user.username || "").toLowerCase();
    const managerU = (rel.manager.username || "").toLowerCase();
    return userName.includes(q) || managerName.includes(q) || userU.includes(q) || managerU.includes(q);
  });
});

function fullName(u) {
  if (!u) return "—";
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

function userLabel(u) {
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  if (name && u.username) return `${name} <${u.username}>`;
  return name || u.username || "";
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString();
}

async function loadAll() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const [relData, unassignedData, usersResp] = await Promise.all([
      getTimecardManagerRelationships(token),
      getUnassignedTimecardManagerUsers(token),
      getAllUsersWithGroups(token),
    ]);
    relationships.value = Array.isArray(relData) ? relData : (relData.results || []);
    unassignedUsers.value = (unassignedData || []).map((u) => ({
      ...u,
      full_name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username,
    }));
    const users = usersResp && usersResp.data ? usersResp.data : [];
    allUsers.value = users
      .filter((u) => {
        const hasGroups = Array.isArray(u.groups) && u.groups.length > 0;
        const isTestUser = (u.username || "").startsWith("dev_test_exp_");
        return hasGroups || isTestUser;
      })
      .map((u) => ({
        ...u,
        full_name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username,
      }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  } catch (error) {
    console.log(error);
    $toast.open({ message: "Failed to load timecard managers", type: "error", duration: 3000 });
  }
  loading.value = false;
}

function openAssignModal(user = null) {
  modalUserId.value = user ? user.id : null;
  modalManagerId.value = null;
  modalLockedUser.value = !!user;
  modalError.value = "";
  modalOpen.value = true;
}

function closeModal() {
  modalOpen.value = false;
  modalError.value = "";
}

async function submitModal() {
  if (!modalUserId.value || !modalManagerId.value) return;
  if (modalUserId.value === modalManagerId.value) {
    modalError.value = "A user cannot be their own timecard manager.";
    return;
  }
  modalSaving.value = true;
  modalError.value = "";
  const token = store.state.auth.token;
  try {
    await createTimecardManagerRelationship(token, {
      user_id: modalUserId.value,
      manager_id: modalManagerId.value,
    });
    $toast.open({ message: "Manager assigned", type: "success", duration: 2000 });
    closeModal();
    await loadAll();
  } catch (error) {
    const detail = error.response && error.response.data && JSON.stringify(error.response.data);
    modalError.value = detail || "Failed to save relationship";
  }
  modalSaving.value = false;
}

async function confirmDeactivate(rel) {
  if (!window.confirm(`Deactivate ${fullName(rel.user)} → ${fullName(rel.manager)}?\n\nUntil a new manager is assigned, this user's submitted timecards will fall back to the default recipient.`)) {
    return;
  }
  const token = store.state.auth.token;
  try {
    await deactivateTimecardManagerRelationship(token, rel.id);
    $toast.open({ message: "Relationship deactivated", type: "success", duration: 2000 });
    await loadAll();
  } catch (error) {
    console.log(error);
    $toast.open({ message: "Failed to deactivate", type: "error", duration: 3000 });
  }
}

onMounted(loadAll);
</script>
